import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  getAllPresupuestoA3Pedidos,
} from "../../api/Produccion/a3ArticulosApi";
import {
  fusionarLogisticaEnvio,
  getLogisticaEnviosResumen,
  vincularPedidoALogisticaEnvio,
} from "../../api/Logistica/logisticaEnviosApi";
import {
  enviosAjenosDePedido,
  getLogisticaEnvioMergeLabel,
} from "./logisticaUtils";

function isA3Served(estado, detalle) {
  const s = String(estado || "")
    .trim()
    .toUpperCase();
  const d = String(detalle || "")
    .trim()
    .toUpperCase();
  return s === "S" || d === "S" || s.includes("SERV") || d.includes("SERV");
}

const normStr = (s) => String(s ?? "").trim();

const listPaperSx = (theme) => ({
  maxHeight: 360,
  overflow: "auto",
  borderRadius: 2,
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  bgcolor: alpha(theme.palette.background.paper, 0.95),
  "& .MuiListItemButton-root": {
    borderRadius: 1,
    mx: 0.5,
    my: 0.25,
  },
  "& .MuiListItemButton-root.Mui-selected": {
    bgcolor: alpha(theme.palette.primary.main, 0.12),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
});

/**
 * Vincular a este envío: pedidos aún sin logística, o fusionar un envío existente
 * (parcial del mismo PO u otro pedido que ya tenga su propia logística).
 */
const VincularPedidoLogisticaDialog = ({
  open,
  onClose,
  idLogisticaEnvio,
  productId,
  anchorCodPros = [],
  excludedIds = [],
  onSuccess,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  /** Si true y hay CodPro de ancla, la lista solo muestra ese proveedor. Por defecto false = todos los productos. */
  const [filterBySupplier, setFilterBySupplier] = useState(false);
  /** Por defecto se listan también servidos en A3 (hace falta para agrupar POs del mismo contenedor). */
  const [hideServed, setHideServed] = useState(false);
  const [tab, setTab] = useState("pedidos");
  const [enviosRows, setEnviosRows] = useState([]);
  const [selectedEnvioId, setSelectedEnvioId] = useState(null);

  const excludedSet = useMemo(
    () => new Set((excludedIds || []).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)),
    [excludedIds],
  );

  const anchorSet = useMemo(() => {
    const s = new Set();
    for (const c of anchorCodPros || []) {
      const n = normStr(c);
      if (n) s.add(n);
    }
    return s;
  }, [anchorCodPros]);

  const hasAnchor = anchorSet.size > 0;
  const pid = Number(productId);
  const productOk = Number.isFinite(pid) && pid > 0;

  const eligible = useMemo(() => {
    const list = Array.isArray(allRows) ? allRows : [];
    return list.filter((r) => {
      if (!r?.IdPresupuestoA3Pedido) return false;
      if (excludedSet.has(Number(r.IdPresupuestoA3Pedido))) return false;
      if (!(r?.IdPedv != null && Number(r.IdPedv) > 0)) return false;
      if (hideServed && isA3Served(r.EstadoVenta, r.EstadoDetalleVenta)) return false;

      if (hasAnchor && filterBySupplier) {
        const cp = normStr(r.CodPro);
        return Boolean(cp) && anchorSet.has(cp);
      }
      return true;
    });
  }, [allRows, excludedSet, hasAnchor, filterBySupplier, hideServed, anchorSet]);

  const filtered = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return eligible;
    return eligible.filter((r) => {
      const blob = [
        r.Referencia,
        r.ClienteNombre,
        r.CodCli,
        r.CodArt,
        r.CodPro,
        r.NombrePresupuesto,
        r.ProyectoSubject,
        r.IdPresupuestoA3Pedido,
        r.IdPresupuesto,
        r.IdProducto,
        r.NumDocVenta,
        r.SerieVenta,
      ]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return blob.includes(q);
    });
  }, [eligible, search]);

  const otherEnvios = useMemo(() => {
    const idActual = Number(idLogisticaEnvio);
    return (Array.isArray(enviosRows) ? enviosRows : []).filter((e) => {
      const idE = Number(e?.IdLogisticaEnvio);
      if (!Number.isFinite(idE) || idE <= 0) return false;
      if (idE === idActual) return false;
      if (e?.IsClosed) return false;
      return true;
    });
  }, [enviosRows, idLogisticaEnvio]);

  const filteredEnvios = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return otherEnvios;
    return otherEnvios.filter((e) => {
      const blob = [
        e.IdLogisticaEnvio,
        e.POReference,
        e.ShipmentCodeCustomer,
        e.BLAWBNr,
        e.Forwarder,
        ...(Array.isArray(e.pedidoLinks) ? e.pedidoLinks : []).flatMap((l) => [
          l?.IdPresupuestoA3Pedido,
          l?.pedidoA3?.Referencia,
          l?.pedidoA3?.ClienteNombre,
          l?.pedidoA3?.CodCli,
          l?.pedidoA3?.CodArt,
        ]),
      ]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return blob.includes(q);
    });
  }, [otherEnvios, search]);

  const selectedEnviosDelPedido = useMemo(
    () => enviosAjenosDePedido(enviosRows, selectedId, idLogisticaEnvio),
    [enviosRows, selectedId, idLogisticaEnvio],
  );

  const selectedEnvio = useMemo(() => {
    const id = Number(selectedEnvioId);
    return (
      otherEnvios.find((e) => Number(e.IdLogisticaEnvio) === id) || null
    );
  }, [otherEnvios, selectedEnvioId]);

  const selected = useMemo(() => {
    const id = Number(selectedId);
    const list = Array.isArray(allRows) ? allRows : [];
    return list.find((x) => Number(x.IdPresupuestoA3Pedido) === id) || null;
  }, [allRows, selectedId]);

  const reset = useCallback(() => {
    setSearch("");
    setSelectedId(null);
    setError("");
    setSubmitting(false);
    setFilterBySupplier(false);
    setHideServed(false);
    setTab("pedidos");
    setSelectedEnvioId(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [data, envios] = await Promise.all([
        getAllPresupuestoA3Pedidos(2000),
        getLogisticaEnviosResumen({ limit: 500, includeClosed: false }),
      ]);
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setAllRows(rows);
      setEnviosRows(Array.isArray(envios) ? envios : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setAllRows([]);
      setEnviosRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      setAllRows([]);
      setEnviosRows([]);
      return;
    }
    reset();
    load();
  }, [open, load, reset]);

  const handleVincular = async () => {
    if (!idLogisticaEnvio) return;
    setSubmitting(true);
    setError("");
    try {
      if (tab === "envios") {
        const idOrig = Number(selectedEnvioId);
        if (!idOrig) return;
        await fusionarLogisticaEnvio(idLogisticaEnvio, idOrig);
      } else {
        const idPed = Number(selectedId);
        if (!idPed) return;
        const ajenos = enviosAjenosDePedido(enviosRows, idPed, idLogisticaEnvio);
        if (ajenos.length === 1) {
          await fusionarLogisticaEnvio(
            idLogisticaEnvio,
            Number(ajenos[0].IdLogisticaEnvio),
          );
        } else if (ajenos.length > 1) {
          setError(t("logisticaPedidos.vincularNeedPickEnvio"));
          setTab("envios");
          setSubmitting(false);
          return;
        } else {
          await vincularPedidoALogisticaEnvio(idLogisticaEnvio, idPed);
        }
      }
      if (onSuccess) await onSuccess();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const anchorLabel = [...anchorSet].join(", ") || "—";
  const willMergeFromPedido = tab === "pedidos" && selectedEnviosDelPedido.length === 1;
  const canSubmit =
    tab === "envios" ? Boolean(selectedEnvioId) : Boolean(selectedId);
  const actionLabel = submitting
    ? tab === "envios" || willMergeFromPedido
      ? t("logisticaPedidos.vincularMerging")
      : t("logisticaPedidos.vincularLinking")
    : tab === "envios" || willMergeFromPedido
      ? t("logisticaPedidos.vincularMerge")
      : t("logisticaPedidos.link");

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{t("logisticaPedidos.vincularDialogTitle")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            {t("logisticaPedidos.vincularEnvioLine", {
              envio: idLogisticaEnvio ?? "—",
              product: productOk ? pid : "—",
            })}
          </Typography>
          <Alert severity="info">{t("logisticaPedidos.vincularRuleOpen")}</Alert>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={tab}
            onChange={(_, v) => {
              if (!v) return;
              setTab(v);
              setSelectedId(null);
              setSelectedEnvioId(null);
              setError("");
            }}
          >
            <ToggleButton value="pedidos">
              {t("logisticaPedidos.vincularTabPedidos")}
            </ToggleButton>
            <ToggleButton value="envios">
              {t("logisticaPedidos.vincularTabEnvios")}
            </ToggleButton>
          </ToggleButtonGroup>
          {tab === "pedidos" ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideServed}
                  onChange={(e) => {
                    setHideServed(e.target.checked);
                    setSelectedId(null);
                  }}
                  color="primary"
                />
              }
              label={t("logisticaPedidos.vincularFilterServed")}
            />
          ) : null}
          {hasAnchor ? (
            <Alert severity="info" variant="outlined">
              {t("logisticaPedidos.vincularRuleAnchorHint", { prov: anchorLabel })}
            </Alert>
          ) : null}
          <Alert severity="warning">{t("logisticaPedidos.vincularWeightsHint")}</Alert>
          {error ? (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          ) : null}

          {hasAnchor ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterBySupplier}
                  onChange={(e) => {
                    setFilterBySupplier(e.target.checked);
                    setSelectedId(null);
                  }}
                  color="primary"
                />
              }
              label={t("logisticaPedidos.vincularFilterSupplier")}
            />
          ) : null}

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
            <Box sx={{ flex: "1 1 48%", minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {tab === "envios"
                  ? t("logisticaPedidos.vincularLeftEnviosTitle")
                  : t("logisticaPedidos.vincularLeftTitle")}
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder={
                  tab === "envios"
                    ? t("logisticaPedidos.vincularSearchEnvioPh")
                    : t("logisticaPedidos.vincularSearchPh")
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Paper variant="outlined" sx={listPaperSx(theme)}>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : tab === "envios" ? (
                  filteredEnvios.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      {t("logisticaPedidos.vincularEmptyEnvios")}
                    </Typography>
                  ) : (
                    <List dense disablePadding sx={{ py: 0.5 }}>
                      {filteredEnvios.map((e) => {
                        const idE = Number(e.IdLogisticaEnvio);
                        const isSel = Number(selectedEnvioId) === idE;
                        const nPed = Array.isArray(e.pedidoLinks)
                          ? e.pedidoLinks.length
                          : 0;
                        return (
                          <ListItemButton
                            key={idE}
                            selected={isSel}
                            onClick={() => setSelectedEnvioId(idE)}
                          >
                            <ListItemText
                              primary={
                                <Typography component="span" fontWeight={700}>
                                  {getLogisticaEnvioMergeLabel(e)}
                                </Typography>
                              }
                              secondary={
                                <Typography component="span" variant="caption">
                                  {t("logisticaPedidos.vincularEnvioMeta", {
                                    pedidos: nPed,
                                    method: e.ShipmentMethod || "—",
                                  })}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  )
                ) : filtered.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {hasAnchor && filterBySupplier
                      ? t("logisticaPedidos.vincularEmptySupplier")
                      : t("logisticaPedidos.vincularEmptyList")}
                  </Typography>
                ) : (
                  <List dense disablePadding sx={{ py: 0.5 }}>
                    {filtered.map((r) => {
                      const id = Number(r.IdPresupuestoA3Pedido);
                      const isSel = Number(selectedId) === id;
                      const served = isA3Served(r.EstadoVenta, r.EstadoDetalleVenta);
                      const enviosPed = enviosAjenosDePedido(
                        enviosRows,
                        id,
                        idLogisticaEnvio,
                      );
                      return (
                        <ListItemButton
                          key={id}
                          selected={isSel}
                          onClick={() => setSelectedId(id)}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                                <Typography component="span" fontWeight={700}>
                                  {r.Referencia || t("logisticaPedidos.vincularSinRef")}
                                </Typography>
                                <Chip size="small" label={`CRM #${id}`} variant="outlined" />
                                {enviosPed.length ? (
                                  <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label={t("logisticaPedidos.vincularHasEnvioChip", {
                                      envios: enviosPed
                                        .map((x) => `#${x.IdLogisticaEnvio}`)
                                        .join(", "),
                                    })}
                                  />
                                ) : null}
                                {served ? (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label={t("logisticaPedidos.vincularServedChip")}
                                  />
                                ) : null}
                              </Stack>
                            }
                            secondary={
                              <>
                                <Typography component="span" variant="caption" display="block">
                                  {r.ClienteNombre || r.CodCli || "—"} · {r.CodArt || "—"} · Prov.{" "}
                                  {r.CodPro || "—"}
                                </Typography>
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {r.NombrePresupuesto || "—"}
                                  {r.ProyectoSubject ? ` · ${r.ProyectoSubject}` : ""} · ProductId{" "}
                                  {r.IdProducto ?? "—"}
                                </Typography>
                              </>
                            }
                            secondaryTypographyProps={{ component: "div" }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Box>

            <Box sx={{ flex: "1 1 48%", minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {t("logisticaPedidos.vincularRightTitle")}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minHeight: 200 }}>
                {tab === "envios" ? (
                  !selectedEnvio ? (
                    <Typography variant="body2" color="text.secondary">
                      {t("logisticaPedidos.vincularPickEnvioHint")}
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      <Typography variant="body1" fontWeight={700}>
                        {getLogisticaEnvioMergeLabel(selectedEnvio)}
                      </Typography>
                      <Alert severity="warning" variant="outlined">
                        {t("logisticaPedidos.vincularMergeHint", {
                          origen: selectedEnvio.IdLogisticaEnvio,
                          destino: idLogisticaEnvio,
                        })}
                      </Alert>
                    </Stack>
                  )
                ) : !selected ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("logisticaPedidos.vincularPickHint")}
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    <Typography variant="body1" fontWeight={700}>
                      {selected.Referencia || "—"}
                    </Typography>
                    <Typography variant="body2">
                      {t("logisticaPedidos.vincularCliente")}: {selected.ClienteNombre || selected.CodCli || "—"}
                    </Typography>
                    <Typography variant="body2">
                      {t("logisticaPedidos.vincularArticulo")}: {selected.CodArt || "—"}
                    </Typography>
                    <Typography variant="body2">
                      {t("logisticaPedidos.vincularProv")}: {selected.CodPro || "—"}
                    </Typography>
                    {willMergeFromPedido ? (
                      <Alert severity="warning" variant="outlined">
                        {t("logisticaPedidos.vincularMergeHint", {
                          origen: selectedEnviosDelPedido[0].IdLogisticaEnvio,
                          destino: idLogisticaEnvio,
                        })}
                      </Alert>
                    ) : null}
                    {selectedEnviosDelPedido.length > 1 ? (
                      <Alert severity="info" variant="outlined">
                        {t("logisticaPedidos.vincularNeedPickEnvio")}
                      </Alert>
                    ) : null}
                    {isA3Served(selected.EstadoVenta, selected.EstadoDetalleVenta) ? (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={t("logisticaPedidos.vincularServedChip")}
                        sx={{ alignSelf: "flex-start" }}
                      />
                    ) : null}
                    <Typography variant="body2" color="text.secondary">
                      {t("logisticaPedidos.vincularPresu")}: {selected.NombrePresupuesto || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      IdPresupuesto: {selected.IdPresupuesto ?? "—"} · ProductId: {selected.IdProducto ?? "—"} ·
                      Units: {selected.Units ?? "—"}
                    </Typography>
                  </Stack>
                )}
              </Paper>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t("logisticaPedidos.cancel")}
        </Button>
        <Button variant="contained" onClick={handleVincular} disabled={submitting || !canSubmit}>
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VincularPedidoLogisticaDialog;
